import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
  ArrowLeft, TrendingUp, Target, Calendar, Loader2, 
  DollarSign, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#9333EA', '#A855F7', '#C084FC', '#D8B4FE', '#7C3AED', '#6D28D9'];

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/analytics`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-vault flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#9333EA] mx-auto mb-4" />
          <p className="font-mono text-[#A19DB0]">[ LOADING ANALYTICS ]</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#12121A] border border-white/10 rounded-md p-3">
          <p className="text-[#A19DB0] text-xs font-bold uppercase tracking-wider mb-1">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} className="text-white text-sm font-mono">
              {entry.name}: {entry.name === 'amount' ? `$${entry.value.toFixed(2)}` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-vault">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                data-testid="back-to-dashboard-button"
                onClick={() => navigate('/dashboard')}
                className="text-[#A19DB0] hover:text-white hover:bg-white/5"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <h1 className="font-heading text-xl font-bold text-white tracking-tight">
                Savings Analytics
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mb-8">
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="h-5 w-5 text-[#9333EA]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0]">Total Deposits</span>
            </div>
            <div data-testid="analytics-total-deposits" className="font-mono text-3xl font-bold text-white">
              {analytics?.total_deposits || 0}
            </div>
          </div>
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <div className="flex items-center gap-3 mb-2">
              <Target className="h-5 w-5 text-[#9333EA]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0]">Active Goals</span>
            </div>
            <div data-testid="analytics-total-goals" className="font-mono text-3xl font-bold text-white">
              {analytics?.total_goals || 0}
            </div>
          </div>
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-[#9333EA]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0]">Months Tracked</span>
            </div>
            <div className="font-mono text-3xl font-bold text-white">
              {analytics?.timeline?.filter(t => t.deposits > 0).length || 0}
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-8">
          {/* Monthly Deposits Bar Chart */}
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <h3 className="font-heading text-lg font-semibold text-white mb-1">Monthly Deposits</h3>
            <p className="text-xs text-[#A19DB0] mb-6">Amount deposited per month</p>
            <div className="h-64" data-testid="monthly-deposits-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.timeline || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#A19DB0" 
                    fontSize={11} 
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis stroke="#A19DB0" fontSize={11} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" fill="#9333EA" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Deposit Count Line Chart */}
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <h3 className="font-heading text-lg font-semibold text-white mb-1">Deposit Frequency</h3>
            <p className="text-xs text-[#A19DB0] mb-6">Number of deposits per month</p>
            <div className="h-64" data-testid="deposit-frequency-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics?.timeline || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#A19DB0" 
                    fontSize={11} 
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis stroke="#A19DB0" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="deposits" 
                    stroke="#9333EA" 
                    strokeWidth={2} 
                    dot={{ fill: '#9333EA', r: 4 }}
                    activeDot={{ r: 6, fill: '#A855F7' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Goal Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-8">
          {/* Pie Chart */}
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <h3 className="font-heading text-lg font-semibold text-white mb-1">Goal Breakdown</h3>
            <p className="text-xs text-[#A19DB0] mb-6">Distribution of savings across goals</p>
            <div className="h-64" data-testid="goal-breakdown-chart">
              {analytics?.goal_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics.goal_breakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="current"
                      nameKey="name"
                      stroke="none"
                    >
                      {analytics.goal_breakdown.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => `$${value.toFixed(2)}`}
                      contentStyle={{ background: '#12121A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                    <Legend 
                      wrapperStyle={{ color: '#A19DB0', fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-[#A19DB0]">
                  No goal data yet
                </div>
              )}
            </div>
          </div>

          {/* Projections */}
          <div className="border-grid p-6 bg-[#0A0A0F]">
            <h3 className="font-heading text-lg font-semibold text-white mb-1">Goal Projections</h3>
            <p className="text-xs text-[#A19DB0] mb-6">Estimated completion dates</p>
            <div className="space-y-3" data-testid="goal-projections">
              {analytics?.projections?.length > 0 ? (
                analytics.projections.map((proj, i) => (
                  <div key={i} className="p-4 bg-[#12121A] rounded-md border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-heading font-semibold text-white">{proj.name}</span>
                      <span className={`text-xs font-bold uppercase tracking-wider ${
                        proj.status === 'completed' ? 'text-emerald-400' :
                        proj.status === 'on_track' ? 'text-[#9333EA]' :
                        proj.status === 'behind' ? 'text-amber-400' :
                        'text-[#A19DB0]'
                      }`}>
                        {proj.status.replace('_', ' ')}
                      </span>
                    </div>
                    {proj.projected_date && (
                      <div className="flex items-center gap-2 text-sm text-[#A19DB0]">
                        <Calendar className="h-3 w-3" />
                        Est. completion: {new Date(proj.projected_date).toLocaleDateString()}
                      </div>
                    )}
                    {proj.daily_rate && (
                      <div className="flex items-center gap-2 text-sm text-[#A19DB0] mt-1">
                        <TrendingUp className="h-3 w-3" />
                        Saving ${proj.daily_rate}/day
                      </div>
                    )}
                    {proj.status === 'insufficient_data' && (
                      <p className="text-xs text-[#A19DB0] mt-1">Need more deposits to project</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center text-[#A19DB0] py-8">No goals to project</div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
